import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '@workramp/ui';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { media, theme } from '../../../styles';
import { certOverviewContent } from '../common/academy-styles';
import TruncatedDiv from '../../widget/truncated-div';

export default class AcademyCertGuideOverview extends Component {
  constructor(props) {
    super(props);
    const expandedSections = {};
    _.each(props.guide.courseSections, (section) => {
      expandedSections[section.id] = true;
    });

    this.state = {
      expandedSections,
    };
  }

  render() {
    const { guide, guideExpanded } = this.props;

    return (
      <TableWrapper
        guideExpanded={guideExpanded}
        role="treegrid"
        aria-readonly="true"
        aria-multiselectable="true"
        className={academyCustomCSS.overview.contentToggledContent}
      >
        {_.map(
          _.sortBy(guide.courseSections, (section) => section.order),
          (section) => this.renderSection(section),
        )}
      </TableWrapper>
    );
  }

  renderSection = (section) => {
    const isExpanded = this.state.expandedSections[section.id];
    const TRANSLATED_EXPANDED_CONTENT_ALERT_PLURAL_KEY = 'expanded_content_alert_plural';
    const TRANSLATED_EXPANDED_CONTENT_ALERT_SINGULAR_KEY = 'expanded_content_alert_singular';
    const numOfTasks = section.courseSectionTasks.length;
    const TRANSLATED_EXPANDED_CONTENT_ALERT = I18n.t(
      `js.academy.certifications.content_item.${
        numOfTasks > 1 ? TRANSLATED_EXPANDED_CONTENT_ALERT_PLURAL_KEY : TRANSLATED_EXPANDED_CONTENT_ALERT_SINGULAR_KEY
      }`,
      {
        num_of_tasks: numOfTasks,
      },
    );

    return (
      <SectionWrapper key={section.id}>
        <VisuallyHidden role="region" aria-live="polite">
          {isExpanded ? <p>{`Section was expanded to show ${TRANSLATED_EXPANDED_CONTENT_ALERT}`}</p> : ''}
        </VisuallyHidden>
        <SectionHeader
          expanded={isExpanded}
          onClick={() => this.toggleSectionExpand(section.id)}
          className={academyCustomCSS.overview.guideSection}
          role="row"
          aria-expanded={isExpanded}
          aria-controls={section.id}
        >
          <SectionTitle role="columnheader">
            <TruncatedDiv text={section.title || 'Untitled'} numLines={2} />
          </SectionTitle>
        </SectionHeader>
        <SectionBody
          expanded={isExpanded}
          role="row"
          id={section.id}
          className={academyCustomCSS.overview.guideTaskTitleWrapper}
        >
          {_.map(
            _.sortBy(section.courseSectionTasks, (task) => task.order),
            (task) => this.renderTask(task, isExpanded),
          )}
        </SectionBody>
      </SectionWrapper>
    );
  };

  renderTask = (task, isExpanded) => {
    const taskTitle = <TaskTitle>{task.title || 'Untitled'}</TaskTitle>;

    return (
      <TaskWrapper className={academyCustomCSS.overview.guideTaskTitle} key={task.id} role="gridcell">
        {isExpanded && this.renderTaskCompletionStatusDot(task)}
        {isExpanded && <span>{taskTitle}</span>}
      </TaskWrapper>
    );
  };

  renderTaskCompletionStatusDot = (task) => {
    const isComplete = this.props.assignment && this.props.assignment.completedTaskIds.includes(task.id);

    return (
      <TaskCompletionStatusDot
        isComplete={isComplete}
        color={this.props.themeOptions.accentColor}
        className={academyCustomCSS.overview.completionStatusDot}
      />
    );
  };

  toggleSectionExpand = (sectionId) => {
    this.setState({
      expandedSections: {
        ...this.state.expandedSections,
        [sectionId]: !this.state.expandedSections[sectionId],
      },
    });
  };
}

const TableWrapper = styled.div`
  border-left: 1px solid #cbcbcb;
  border-right: 1px solid #cbcbcb;
  border-bottom: 1px solid #cbcbcb;
  display: ${(props) => (props.guideExpanded ? '' : 'none')};
`;

const SectionWrapper = styled.div``;

const SectionHeader = styled.button`
  width: 100%;
  border-left: none;
  border-right: none;
  background-color: #f9f9f9;
  border-top: 1px solid #cbcbcb;
  border-bottom: 1px solid #cbcbcb;
  padding: 1rem ${certOverviewContent.paddingLeftRight};
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props) => !props.expanded && 'border-bottom: none;'}

  ${media.desktopMedium} {
    padding-left: ${certOverviewContent.paddingLeftRightDesktopMedium};
    padding-right: ${certOverviewContent.paddingLeftRightDesktopMedium};
  }

  ${media.mobile} {
    padding-left: ${certOverviewContent.paddingLeftRightMobile};
    padding-right: ${certOverviewContent.paddingLeftRightMobile};
  }
`;

const SectionTitle = styled.div`
  color: ${theme.color.textPrimary};
  font-size: 1rem;
  ${theme.font.semibold}
  margin-right: 2rem;
`;

const SectionBody = styled.div`
  box-sizing: border-box;
  overflow: auto;
  height: 0;
  transition:
    padding-top 0.1s linear,
    padding-bottom 0.1s linear;

  ${(props) => props.expanded && 'height: auto;'}
  ${(props) =>
    props.expanded &&
    `
    padding: 20px ${certOverviewContent.paddingLeftRight};
    ${media.desktopMedium} {
      padding-left: ${certOverviewContent.paddingLeftRightDesktopMedium};
      padding-right: ${certOverviewContent.paddingLeftRightDesktopMedium};
    }

    ${media.mobile} {
      padding-left: ${certOverviewContent.paddingLeftRightMobile};
      padding-right: ${certOverviewContent.paddingLeftRightMobile};
    }
    `}
`;

const TaskWrapper = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
`;

const TaskTitle = styled.div``;

const TaskCompletionStatusDot = styled.div`
  box-sizing: border-box;
  height: 13px;
  width: 13px;
  background-color: ${(props) => (props.isComplete ? props.color : 'transparent')};
  border-radius: 50%;
  border: 1px solid ${(props) => (props.isComplete ? props.color : '#979797')};
  margin-right: 1.5rem;
  flex: none;
`;
